import { forwardRef } from 'react';
import { ExcelExport as KendoExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';

/**
 * ExcelExport component wraps KendoReact's ExcelExport and provides
 * a convenient way to export tabular data to Excel format.
 * 
 * @see {@link https://www.telerik.com/kendo-react-ui/components/excelexport | Kendo React UI ExcelExport}
 * @todo update component with remaining API props and methods from docs
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.data - data to export.
 * @param {Array<Object>} props.columns - Array of column definitions. Each column should have:
 *   - {string} field - field name in data.
 *   - {string} title - column header.
 *   - {number|string} [width] - column width (number or numeric string).
 *   - {boolean} [isHiddenFromExport] - If true, column will not be exported.
 * @param {string} [props.fileName='Export'] - name of exported Excel file.
 * @param {Array<Object>} [props.group] - Optional grouping configuration.
 * @param {React.ReactNode} [props.children] - Additional children to render inside export.
 * @param {React.Ref} ref - Ref forwarded to KendoExcelExport component.
 * @returns {JSX.Element} rendered ExcelExport component.
 */
export const ExcelExport = forwardRef(({
  data,
  columns,
  fileName = 'Export',
  group,
  children,
}, ref) => {
  return (
    <KendoExcelExport
      data={data}
      ref={ref}
      fileName={fileName}
      group={group}
    >
      {columns && columns.length > 0 && columns.map((column, index) => (
        <ExcelExportColumn
          key={index}
          field={column.field}
          title={column.title}
          width={column.width !== undefined ? parseInt(column.width, 10) : undefined} // protects from NaN
          hidden={column.isHiddenFromExport} // use this property to control export visibility in column config
        />
      ))}
      {children}
    </KendoExcelExport>
  );
});